/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SecurityContext, ViewChild
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {merge, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {ChangeTrackingChanges} from './change-tracking-changes';
import {ChangeTrackingService, TrackingSources} from './change-tracking.service';

import {Destroyable} from '../../destroyable.component';
import {RolesService} from '../authentication/roles/roles.service';
import {LocalizationService} from '../localization/localization.service';
import {SdcEventLogContext, SdcLogDTO} from '../sdconnect/sdconnect.model';
import {SdconnectService} from '../sdconnect/sdconnect.service';
import {DefaultPagination, Pagination} from '../ui/pagination.component';
import {SortDirection} from '../ui/table/table.constants';
import {SortKey} from '../ui/table/table.interfaces';
import {User, UserService} from '../user';
import {isBlank, isPresent, isValueConsideredAsEmpty} from '../utils/lang';
import {TreeItem} from "../ui/tree-multi-select/treeview.component";
import {DropdownTreeviewComponent, DropdownTreeviewConfig} from "../ui/tree-multi-select/dropdown-treeview.component";
import isEmpty from "lodash-es/isEmpty";

@Component({
  selector: 'gp-change-tracking',
  templateUrl: './change-tracking.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./change-tracking.component.scss']
})

export class ChangeTrackingComponent extends Destroyable implements OnInit {
  public trackingSources = [
    { id: TrackingSources.GENERAL, transId: 'CHANGE_TRACKING.general', tooltip: 'CHANGE_TRACKING.general' },
    { id: TrackingSources.VARIABLE_ELEMENTS, transId: 'CHANGE_TRACKING.variable', tooltip: 'CHANGE_TRACKING.variable' }
  ];

  public isValueConsideredAsEmpty = isValueConsideredAsEmpty;

  public logs: any[] = [];

  @Input()
  public noChangeMessage: string = 'CHANGE_TRACKING.no_change_tracks';

  @Input()
  public hideTitle = false;

  @Input()
  public hideTabs = false;

  @ViewChild('itemFilterComponent')
  public itemFilterComponent: DropdownTreeviewComponent;

  @Output()
  public init: EventEmitter<(entityId: number, entityType: string, itemType?: string) => void> =
    new EventEmitter<(entityId: number, entityType: string, itemType?: string) => void>();

  public itemList: { id: number; select: string; name: string; trackingSource: TrackingSources }[] = [
    { id: 1, select: 'EMP_DATA', name: 'CHANGE_TRACKING.data', trackingSource: TrackingSources.GENERAL },
    { id: 2, select: 'CONTRACT_FLATTEN', name: 'CHANGE_TRACKING.inheritedData', trackingSource: TrackingSources.GENERAL },
    { id: 3, select: 'EMP_VARIABLE', name: 'CHANGE_TRACKING.variable', trackingSource: TrackingSources.VARIABLE_ELEMENTS },
    { id: 4, select: 'PEOPLE_TREE_NODE', name: 'CHANGE_TRACKING.peopleTree', trackingSource: TrackingSources.GENERAL },
    { id: 5, select: 'EMP_ABSENCE', name: 'CHANGE_TRACKING.absence', trackingSource: TrackingSources.GENERAL },
    { id: 6, select: 'REP_DATA', name: 'CHANGE_TRACKING.repData', trackingSource: TrackingSources.GENERAL },
    { id: 7, select: 'REP_ENTITY', name: 'COMMON.entity', trackingSource: TrackingSources.GENERAL },
    { id: 8, select: 'CLI_EMP_DATA', name: 'CHANGE_TRACKING.EmployeeData', trackingSource: TrackingSources.GENERAL },
  ];

  public itemTree: TreeItem[] = [];
  public selectedItems: Set<string> = new Set();
  public treeConfig: DropdownTreeviewConfig = {
    isShowFilter: false,
    isShowCollapseExpand: false,
  };

  public entityId: number;
  public entityType: string;
  public itemType: string;

  public loading: boolean = true;
  public filtersForm: UntypedFormGroup;
  public pagination: Pagination = new DefaultPagination();
  public timezone: string;
  public sdcEventLogs: SdcLogDTO[];
  public isSdconnectEnabledAndHasRole = false;
  private sortKey: SortKey = [{
    direction: 1,
    key: 'updatedate'
  }];
  private filterValues: { date?: string; author?: string; item?: string; groupZoneName?: string; trackingSource?: TrackingSources } = {
    date: '',
    author: '',
    item: '',
    groupZoneName: '',
    trackingSource: TrackingSources.GENERAL,
  };


  constructor(private changeTrackingService: ChangeTrackingService,
              private translate: TranslateService,
              private localization: LocalizationService,
              private cd: ChangeDetectorRef,
              private formBuilder: UntypedFormBuilder,
              private sanitizer: DomSanitizer,
              private userService: UserService,
              private sdConnectAdminService: SdconnectService,
              private roelService: RolesService) {
    super();
    this.filtersForm = this.formBuilder.group({
      date: ['', ''],
      author: ['', ''],
      item: ['', ''],
      groupZoneName: ['', ''],
      trackingSource: [this.filterValues.trackingSource]
    });
    this.sdcEventLogs = [];
    this.isSdconnectEnabledAndHasRole = localStorage.getItem('gp_sdconnect_enabled') === 'true' && this.roelService.hasRole('SDC_CALL_DETAILS');
  }

  public ngOnInit(): void {
    this.initItemTree();
    this.filtersForm.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(400),
        distinctUntilChanged(),
        map((value: any) => {
          value.author = (value.author || '').trim().toLowerCase();
          value.groupZoneName = (value.groupZoneName || '').trim().toLowerCase();
          const isTrackingSourceChanged =
            value.trackingSource && this.filterValues.trackingSource !== value.trackingSource;
          this.filterValues = value;
          return isTrackingSourceChanged;
        })
      )
      .subscribe((isTrackingSourceChanged) => {
        if (isTrackingSourceChanged) {
          this.initItemTree();
        }
        this.fetchLogs();
      });

    this.pagination.update(0);
    merge(
      this.pagination.pageChanged.asObservable(),
      this.translate.onLangChange.asObservable()
    )
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.fetchLogs());
    this.init.emit(this.load.bind(this));

    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((user: User) => {
        this.timezone = user.timezone;
      });
  }

  public load(entityId: number, entityType: string, itemType: string = null): void {
    if (entityType && entityId) {
      this.entityId = entityId;
      this.entityType = entityType;
      this.itemType = itemType;
      this.fetchLogs();
    }
  }

  public onSortKeyChange(key: SortKey): void {
    this.sortKey = key;
    this.fetchLogs();
  }

  public getDisplayValue(value: string): SafeHtml {
    if (!isValueConsideredAsEmpty(value)) {
      value = value + '';
      return this.sanitizer.sanitize(SecurityContext.HTML, this.translate.instant(value));
    }

    return this.sanitizer.bypassSecurityTrustHtml('-');
  }

  public fetchLogs(): void {
    if (!isPresent(this.entityId)) {
      return;
    }

    const sort: string = this.getSort();

    let searchParams = this.entityType;

    if (this.filterValues.author) {
      searchParams = searchParams.slice(0, -1) + `;author=='*${this.filterValues.author}*')`;
    }

    if (this.filterValues.groupZoneName) {
      searchParams = searchParams.slice(0, -1) + `;groupZoneName=='*${this.filterValues.groupZoneName}*')`;
    }

    if (this.selectedItems.size > 0) {
      const itemList = Array.from(this.selectedItems).map(item => `'${item}'`).join(',');
      searchParams = searchParams.slice(0, -1) + `;item=in=(${itemList}))`;
    }

    if (this.filterValues.date) {
      const startOfDay = moment(this.filterValues.date).startOf('day').utc().toISOString();
      const endOfDay = moment(this.filterValues.date).endOf('day').utc().toISOString();

      searchParams = searchParams.slice(0, -1) + `;updatedate=ge='${startOfDay}';updatedate=le='${endOfDay}')`;
    }

    const params: string[] = [
      `search=${searchParams}`
    ];

    if (this.itemType) {
      params.push(`item=${this.itemType}`);
    }

    if (!isBlank(sort) && sort !== '') {
      params.push(`orderBy=${sort}`);
    }

    this.pagination.resetPageIfNeeded({search: searchParams, sort: sort});
    params.push(`perPage=${this.pagination.perPage}`, `page=${this.pagination.currentPage.number}`);

    this.logs = [];
    const eventIds: string [] = [];
    this.loading = true;
    this.changeTrackingService
      .getChangeTrackingData(this.entityId, params.join("&"), this.filterValues.trackingSource)
      .pipe(tap(() => {
          this.loading = false;
        }),
        switchMap((result: { data: any[], total: number }) => {
          this.pagination.update(result.total)
          this.logs = result.data.map((log: any) => {
            const groups: any[] = [];
            groups.push(new ChangeTrackingChanges(
              log,
              this.translate,
              this.localization
            ));

            log.changes = groups;
            const groupNames: Set<string> = new Set<string>();
            groups.forEach((group: any) => {
              groupNames.add(this.translate.instant(group.groupName || '-'));
            });
            log.changes.groupNames = Array.from(groupNames).join(', ');

            if (log.eventId) {
              eventIds.push(log.eventId);
            }

            return log;
          });

          if (this.isSdconnectEnabledAndHasRole && eventIds.length > 0) {
            return this.sdConnectAdminService.getSdcLogsByEventIds(this.logs[0].itemId, eventIds).pipe(
              tap((logs: SdcLogDTO[]) => {
                this.sdcEventLogs = logs;
                this.mergeLogsAndSDCEventLogs();
              }),
              switchMap(() => of(result.total))
            );
          } else {
            return of(result.total);
          }
        }),
        tap((total: number) => {
          this.pagination.update(total);
          this.cd.detectChanges();
        })
      ).subscribe();

  }

  public onItemSelected(itemIds: string[]): void {
    const newSet = new Set(itemIds || []);
    const isDifferent =
      newSet.size !== this.selectedItems.size || Array.from(newSet).some((id) => !this.selectedItems.has(id));
    if (isDifferent) {
      this.selectedItems = newSet;
      this.fetchLogs();
    }
  }


  /**
   * Merges change tracking occurrence and SDC event logs by adding matching event log details when found
   * For each item in the `logs` array, it finds a corresponding sdc event log from the
   * `sdcEventLogs` array based on the `eventId`,`zoneName`,`beginDate` or `groupZoneName`.
   */
  private mergeLogsAndSDCEventLogs(): void {
    this.logs.forEach(log => {
      const eventId = log.eventId;
      // Parse only after zone as the deletion are not synchronized.

      if (!log.after) {
        return;
      }
      // Keep only events with event id matching the current log one.
      const matchingEventLogsByEventId: SdcLogDTO[] = this.sdcEventLogs.filter(eventLog => eventLog.eventId === eventId);

      let matchingEventLog: SdcLogDTO;

      if (matchingEventLogsByEventId.length === 0) {               //  1) No matching event
        return;
      } else if (matchingEventLogsByEventId.length === 1) {        //  2) One matching event
        matchingEventLog = matchingEventLogsByEventId[0];
      } else {                                           //  3) Multiple events
        // Loop through sdcEventLogs to identify the one (if exists) matching the current log
        matchingEventLog = this.matchEventLog(matchingEventLogsByEventId, log);
      }

      if (matchingEventLog) {
        log.sdcEventLogDetails = matchingEventLog;
      }
    });
  }

  /**
   * Matches an SDC event log from a list to a given changeTrack row. Based on the zones and context
   *
   * The matching logic follows these steps:
   * 1. If the event log context contains a `groupZoneName` property:
   *    a. Check if any zone names in the current change track row match the `zoneNames` in the event log context.
   *    b. If no match is found, try to match with the `beginDate` and `groupZoneName`
   * 2. If the sdc event log context does not contain a `groupZoneName` property,
   *    check if any zone names in the change track occurrence are included
   *    in the string representation of the event log context.
   */
  private matchEventLog(matchingEventLogs: SdcLogDTO[], log: any): SdcLogDTO | undefined {

    // Get zone names from after zones
    const changedZones = Object.values(log.after?.zones ?? {}).map((zone: any) => zone.zoneName);

    const instanceOfSdcEventContext = (context: any): context is SdcEventLogContext => {
      return 'groupZoneName' in context;
    };

    for (const currentEventLog of matchingEventLogs) {
      try {
        const context: SdcEventLogContext | string[] = JSON.parse(currentEventLog.context?.toString());

        if (instanceOfSdcEventContext(context)) {
          if (this.containsAnyZone(changedZones, context.zoneNames) ||
            (log.after?.beginDate === context.beginDate && log.after?.groupZoneName === context.groupZoneName)) {
            return currentEventLog;
          }
        } else if (Array.isArray(context)) {
          if (this.containsAnyZone(changedZones, context)) {
            return currentEventLog;
          }
        } else {
          return this.containsAnyZone(changedZones, context) ? currentEventLog : undefined;
        }
      } catch (error) {
        return this.containsAnyZone(changedZones, currentEventLog.context?.toString()) ? currentEventLog : undefined;
      }
    }
    return undefined;
  }

  private containsAnyZone(zones: string[], text: string | string[]): boolean {
    return zones.some(zoneName => text?.includes(zoneName));
  }

  private getSort(): string {
    return this.sortKey.map(element => {
      const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
      return `${prefix}${element.key}`;
    }).join(',');
  }

  public initItemTree(): void {
    const treeRoot = new TreeItem("root");
    treeRoot.items = this.itemList
      .filter((item) => item.trackingSource === this.filterValues.trackingSource)
      .map((item) => new TreeItem(this.translate.instant(item.name), item.select, [item.select]));
    this.itemTree = treeRoot.items;
  }

  public resetFilters(): void {
    if (this.selectedItems.size > 0 || !isEmpty(this.filterValues.date) ||
      !isEmpty(this.filterValues.author) || !isEmpty(this.filterValues.groupZoneName)) {
      this.filtersForm.reset({trackingSource: this.filterValues.trackingSource}, {emitEvent: false});
      this.selectedItems = new Set();
      this.itemFilterComponent?.resetDropdown();
      this.fetchLogs();
    }
  }

  public isFiltering(): boolean {
    return !isEmpty(this.filterValues.date) ||
      !isEmpty(this.filterValues.author) ||
      !isEmpty(this.filterValues.groupZoneName) ||
      this.selectedItems.size > 0;
  }

  public changeTrackingSource(source: TrackingSources): void {
    this.filtersForm.patchValue({trackingSource: source});
  }

  public get showSdcColumns(): boolean {
    return this.itemType === 'EMP' && this.isSdconnectEnabledAndHasRole;
  }
}
